import React, { useRef, useState, Suspense, useEffect } from 'react'
import { Card, Flex, Input, CardFooter, Button, Icon, Text, InputGroup, InputRightElement, Box, Checkbox } from '@chakra-ui/react'
import { ClipboardCheckIcon, Copy03Icon, Trash02Icon, DragGridIcon } from '../../../assets/icons'
import { ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons'
import ParagraphCard from '../ParagraphCard'
import ScaleCard from '../ScaleCard'
import MultipleChoiceCard from '../MultipleChoiceCard'
import URLCard from '../URLCard'
import MediaCard from '../MediaCard'
import SectionCard from '../SectionCard'
import CheckboxCard from '../CheckboxCard'
import { ContentItemType } from '../../../types/content/contentItems'
import { ContentType } from '../../../types/content/content'
import { XCircleIcon, CheckCircleFilledIcon } from '../../../assets/icons'
import { CONTENT_EXPERIENCE } from '../../../pages/user/Content/contentExperienceStyles'

const ResponsePieChart = React.lazy(() => import('../../../components/graphs/ResponsePieChart'))
const ResponseBarChart = React.lazy(() => import('../../../components/graphs/ResponseBarChart'))

interface CustomCardProps {
  id: number
  index: number
  type: ContentItemType
  text: string // Title or Question basically
  mcqAnswers: string[]
  media: File
  points?: number
  paragraph?: string
  short?: string
  label?: string

  correct_feedback?: string
  incorrect_feedback?: string
  contentType?: ContentType
  handleAddQuestion?: (qType: ContentItemType) => void
  handleAddSection?: () => void
  onMCQSelect?: (questionId: string, selectedIndex: number, isCorrect: boolean) => void
  onCheckboxToggle?: (questionId: string, optionIndex: number, isCorrect: boolean) => void
  mcqSelectedIndex?: number | null
  checkboxSelectedIndexes?: number[] | null
  handleDelete?: (id: number) => void
  moveCard?: (dragIndex: number, hoverIndex: number) => void
  handleUpdate?: (updatedFields: {
    type?: string
    text?: string | File
    short?: string
    paragraph?: string
    file?: File
    answers?: string[]
    question?: string
    points?: number
    correct_feedback?: string
    incorrect_feedback?: string
    label?: string
  }) => void
  quizMode?: boolean
  isEditing?: boolean
  quizSubmitted?: boolean
  responseData?: {
    name: string
    value: number
    answerText: string
  }[]
  showResponses?: boolean
  isIndividualResponse?: boolean
  isCorrect?: boolean
  isReadOnly?: boolean
  respondentName?: string
  userAnswer?: string | string[]
  hasUser?: boolean
  onRatingSelect?: (questionId: string, rating: number) => void
  onAnswer?: (questionId: string, answer: string) => void
  ratingValue?: number | null
  isSurveyQuestion?: boolean
  enableEditor?: boolean
  isModal?: boolean
  /** drag-handle props from react-beautiful-dnd – placed on the footer icon */
  dragHandleProps?: Record<string, any>
}

const MasterCard: React.FC<CustomCardProps> = ({
  id,
  index,
  type,
  mcqAnswers,
  points: currentPoints,
  correct_feedback,
  incorrect_feedback,
  handleDelete,
  handleUpdate,
  quizMode = false,
  quizSubmitted = false,
  responseData,
  showResponses = false,
  isEditing = true,
  isIndividualResponse = false,
  isCorrect: externalIsCorrect = false,
  isReadOnly = false,
  userAnswer,
  hasUser,
  onRatingSelect,
  onAnswer,
  ratingValue,
  isSurveyQuestion = false,
  isModal = false,
  dragHandleProps,
  ...props
}) => {
  const currentType = type
  const normalizedType: ContentItemType = (currentType as any) === 'checkbox'
    ? ContentItemType.LearningCheckbox
    : currentType
  const [currentText, setCurrentText] = useState<string | File>(props.text || '')
  const [points, setPoints] = useState<number>(currentPoints ? currentPoints : 0)
  const [answerSelectionMode, setAnswerSelectionMode] = useState(false)
  const [isCorrect, setIsCorrect] = useState<boolean | undefined>(
    externalIsCorrect !== undefined ? externalIsCorrect : undefined,
  )

  useEffect(() => {
    if (externalIsCorrect !== undefined) {
      setIsCorrect(externalIsCorrect)
    }
  }, [externalIsCorrect])

  const mcqRef = useRef<any>(null)

  const isInteractionDisabled = isReadOnly || showResponses || isIndividualResponse || !isEditing
  const displayType = normalizedType === ContentItemType.LearningCheckbox ? 'learning_checkbox' : normalizedType
  const typeLabelMap: Record<string, string> = {
    short: 'Short Response',
    paragraph: 'Learning Content',
    survey_text: 'Written Response',
    media: 'Media',
    survey_multiple_choice: 'Multiple Choice',
    training_multiple_choice: 'Quiz Question',
    training_checkbox: 'Multi-Select Quiz',
    learning_checkbox: 'Completion Check',
    survey_rating: 'Rating Scale',
    url: 'Link',
  }
  const cardTypeLabel = typeLabelMap[displayType] || 'Content Item'
  const showsPromptBadge = ['short', 'paragraph', 'survey_multiple_choice', 'training_multiple_choice', 'training_checkbox', 'checkbox', 'survey_rating', 'survey_text', 'learning_checkbox'].includes(displayType)

  const updatePoints = React.useCallback(
    (newValue: number) => {
      setPoints(newValue)
      handleUpdate?.({ text: currentText, points: newValue })
    },
    [currentText, handleUpdate],
  )

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value
    setCurrentText(newText)
    handleUpdate?.({ type: currentType, text: newText })
  }

  const handleMediaChange = (file: File | null) => {
    if (file) {
      setCurrentText(file)
    }
    handleUpdate?.({ type: currentType, text: file || undefined, file: file || undefined })
  }

  const handleMultipleChoiceAnswersChange = (answers: any) => {
    handleUpdate?.({ type: currentType, text: currentText as string, answers })
  }

  const handleFeedbackChange = (correct_feedback: any, incorrect_feedback: any) => {
    handleUpdate?.({
      type: currentType,
      text: currentText as string,
      correct_feedback: correct_feedback,
      incorrect_feedback: incorrect_feedback,
    })
  }

  const handleURLChange = (url: string) => {
    setCurrentText(url)
    handleUpdate?.({ type: currentType, text: url })
  }

  const handleShortChange = (short: string) => {
    handleUpdate?.({ type: currentType, text: currentText, short: short })
  }

  const handleParagraphChange = (paragraph: string) => {
    handleUpdate?.({ type: currentType, text: currentText, paragraph: paragraph })
  }

  // Local state for LearningCheckbox only
  const [localChecked, setLocalChecked] = React.useState(userAnswer === 'true');
  React.useEffect(() => {
    setLocalChecked(userAnswer === 'true');
  }, [userAnswer]);

  const renderContent = () => {
    switch (normalizedType) {
      case 'short':
        return <ParagraphCard isShort={true} onChange={handleShortChange} short={props.short} isEditing={isEditing} />
      case 'paragraph':
        return (
          <ParagraphCard
            isShort={false}
            onChange={handleParagraphChange}
            paragraph={props.paragraph}
            isEditing={isEditing}
            quizMode={quizMode}
            enableEditor={props.enableEditor}
          />
        )
      case 'survey_text':
        return (
          <ParagraphCard
            isShort={true}
            quizMode={quizMode}
            questionId={String(id)}
            response={userAnswer as string}
            onAnswer={onAnswer}
            isSurvey
            isIndividualResponse={isIndividualResponse}
          />
        )
      case 'media':
        return (
          <MediaCard
            media={props.media}
            onMediaChange={handleMediaChange}
            isReadOnly={isInteractionDisabled}
            quizMode={quizMode}
            isEditing={isEditing}
          />
        )
      case 'survey_multiple_choice':
        return (
          <MultipleChoiceCard
            answers={mcqAnswers || []}
            onAnswersChange={handleMultipleChoiceAnswersChange}
            readOnly={!quizMode || showResponses || (quizSubmitted && quizMode)}
            quizMode={quizMode}
            survey={true}
            quizSubmitted={quizSubmitted}
            questionId={String(id)}
            onMCQSelect={props.onMCQSelect}
            isIndividualResponse={isIndividualResponse}
            userAnswer={userAnswer as string}
            mcqSelectedIndex={props.mcqSelectedIndex}
          />
        )
      case 'training_multiple_choice':
        { const mcqCard = (
          <MultipleChoiceCard
            ref={mcqRef}
            answers={Array.isArray(mcqAnswers) ? JSON.parse(JSON.stringify(mcqAnswers)) : []}
            training={true}
            onAnswersChange={handleMultipleChoiceAnswersChange}
            forceSelectingCorrect={answerSelectionMode}
            onForceCancel={() => setAnswerSelectionMode(false)}
            onForceDone={() => setAnswerSelectionMode(false)}
            quizMode={quizMode}
            quizSubmitted={quizSubmitted}
            questionId={String(id)}
            correct_feedback={correct_feedback}
            incorrect_feedback={incorrect_feedback}
            onFeedbackChange={({ correct_feedback, incorrect_feedback }) =>
              handleFeedbackChange(correct_feedback, incorrect_feedback)
            }
            handleFeedbackDisplay={(isCorrect) => setIsCorrect(isCorrect)}
            onMCQSelect={(questionId, selectedIndex, isCorrect) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              props.onMCQSelect && props.onMCQSelect(questionId, selectedIndex, isCorrect)
            }}
            isIndividualResponse={isIndividualResponse}
            userAnswer={userAnswer as string}
            mcqSelectedIndex={props.mcqSelectedIndex}
            readOnly={!quizMode || showResponses || !isEditing}
          />
        )
        return mcqCard }

      case 'training_checkbox':
        { const checkboxCard = (
          <CheckboxCard
            answers={Array.isArray(mcqAnswers) ? JSON.parse(JSON.stringify(mcqAnswers)) : []}
            training={true}
            onAnswersChange={handleMultipleChoiceAnswersChange}
            forceSelectingCorrect={quizMode || answerSelectionMode}
            onForceCancel={() => setAnswerSelectionMode(false)}
            quizMode={quizMode}
            quizSubmitted={quizSubmitted}
            questionId={String(id)}
            correct_feedback={correct_feedback}
            incorrect_feedback={incorrect_feedback}
            onFeedbackChange={({ correct_feedback, incorrect_feedback }) =>
              handleFeedbackChange(correct_feedback, incorrect_feedback)
            }
            handleFeedbackDisplay={(isCorrect) => setIsCorrect(isCorrect)}
            onCheckboxToggle={(questionId, optionIndex, isCorrect) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              props.onCheckboxToggle && props.onCheckboxToggle(questionId, optionIndex, isCorrect)
            }}
            isIndividualResponse={isIndividualResponse}
            userAnswer={userAnswer as string[]}
            checkboxSelectedIndexes={props.checkboxSelectedIndexes}
            readOnly={!quizMode || showResponses || !isEditing}
          />
        )
        return checkboxCard }
        
      case ContentItemType.LearningCheckbox: {
        const labelText = isIndividualResponse ? (localChecked ? 'Confirmed' : 'Not confirmed') : 'Confirm';
        return (
          <Flex align="center" ml="10px" mr="16px" mt="0px" mb="8px">
            <Checkbox
              isDisabled={!quizMode}
              isChecked={localChecked}
              mr={3}
              onChange={(e) => {
                setLocalChecked(e.target.checked);
                if (quizMode && onAnswer) {
                  onAnswer(String(id), e.target.checked ? 'true' : '');
                }
              }}
            />
            <Box color="solid.900">{labelText}</Box>
          </Flex>
        );
      }
      case 'survey_rating':
        return (
          <ScaleCard
            readOnly={!quizMode || showResponses || (quizSubmitted && quizMode)}
            value={ratingValue}
            questionId={String(id)}
            onRatingSelect={onRatingSelect}
            quizMode={quizMode}
          />
        )
      case 'url':
        return <URLCard value={currentText as string} onChange={handleURLChange} isReadOnly={!isEditing} />
      default:
        return null
    }
  }

  const chartType = index % 2 === 0 ? 'bar' : 'pie'

  if (currentType === 'section') {
    if (quizMode || showResponses || !isEditing) {
      return (
        <Box w="100%" bg="transparent" border="none" boxShadow="none" px={0} py={2}>
          <Text fontSize="18px" fontWeight="700" color="solid.900">
            {typeof currentText === 'string' ? currentText : ''}
          </Text>
        </Box>
      )
    }

    return (
      <Box w="100%" bg="transparent" border="none" boxShadow="none" px={0} py={0}>
        <SectionCard
          title={typeof currentText === 'string' ? currentText : ''}
          onChange={(value) => {
            setCurrentText(value)
            handleUpdate?.({ type: currentType, text: value })
          }}
          handleDelete={handleDelete || (() => { })}
          id={id}
          isDisabled={quizMode || showResponses || !isEditing}
        />
      </Box>
    )
  }

  return (
    <Flex w="100%" direction="row" align="flex-start" className="master-card-container" position="relative">
      <Card
        {...props}
        cursor="default"
        w="100%"
        p="0px"
        border={isModal ? 'none' : '1px solid'}
        borderColor={isModal ? 'transparent' : CONTENT_EXPERIENCE.glassBorder}
        bg={isModal ? 'transparent' : CONTENT_EXPERIENCE.glassPanelBg}
        boxShadow={isModal ? 'none' : CONTENT_EXPERIENCE.glassShadow}
        borderRadius={CONTENT_EXPERIENCE.cardRadius}
        overflow="hidden"
        sx={{ background: isModal ? 'transparent !important' : `${CONTENT_EXPERIENCE.glassPanelBg} !important` }}
      >
        <Flex direction="column">
          <Flex
            justifyContent="left"
            py="20px"
            px={{ base: '14px', md: '18px' }}
            align="flex-start"
            bg="background.White"
          >
            <>
              {showsPromptBadge &&
                index !== undefined && (
                  <Flex
                    minW="42px"
                    h="42px"
                    px="12px"
                    borderWidth="1px"
                    borderColor="solid.100"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="8px"
                    color="solid.700"
                    fontWeight="700"
                    bg="solid.0"
                  >
                    {index + 1}
                  </Flex>
                )}

              {!showResponses && (
                <Flex direction="column" w="100%">
                  <Flex
                    ml="10px"
                    mb={normalizedType !== 'url' && normalizedType !== 'media' ? '10px' : '2px'}
                    align="center"
                    justify="space-between"
                    gap="12px"
                    flexWrap="wrap"
                  >
                    <Text
                      textStyle="h-xxs"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                      color="solid.500"
                    >
                      {cardTypeLabel}
                    </Text>
                    {(currentType === 'training_multiple_choice' || currentType === 'training_checkbox') &&
                      !quizMode &&
                      !showResponses &&
                      !isIndividualResponse &&
                      !isReadOnly && (
                        <Flex
                          align="center"
                          px="10px"
                          py="6px"
                          borderRadius="8px"
                          bg="solid.0"
                          border="1px solid"
                          borderColor="solid.100"
                        >
                          <Text textStyle="h-xxs" color="solid.600" mr="8px">
                            Points
                          </Text>
                          <InputGroup size="sm" minWidth="58px">
                            <Input
                              type="number"
                              textAlign="center"
                              p={0}
                              pr="16px"
                              bg="transparent"
                              border="none"
                              _focus={{ boxShadow: 'none', bg: 'transparent' }}
                              style={{
                                background: 'transparent',
                                width: `${Math.max(42, (String(points).length + 1) * 10)}px`,
                                transition: 'width 0.2s ease',
                              }}
                              value={points}
                              onChange={(e) => {
                                const newVal = Number(e.target.value)
                                updatePoints(newVal)
                              }}
                            />
                            <InputRightElement width="auto" mr={0} display="flex" alignItems="center">
                              <Flex direction="column" justifyContent="center">
                                <Button
                                  size="xxs"
                                  p={0}
                                  variant="hoverEffect"
                                  minW="auto"
                                  onClick={() => {
                                    const newVal = points + 5
                                    updatePoints(newVal)
                                  }}
                                >
                                  <Icon boxSize="14px" color="solid.400" fill="none">
                                    <ChevronUpIcon />
                                  </Icon>
                                </Button>
                                <Button
                                  size="xxs"
                                  p={0}
                                  variant="hoverEffect"
                                  minW="auto"
                                  onClick={() => {
                                    const newVal = Math.max(points - 5, 0)
                                    updatePoints(newVal)
                                  }}
                                >
                                  <Icon boxSize="14px" color="solid.400" fill="none">
                                    <ChevronDownIcon />
                                  </Icon>
                                </Button>
                              </Flex>
                            </InputRightElement>
                          </InputGroup>
                        </Flex>
                      )}
                  </Flex>
                  {!showResponses && normalizedType !== 'url' && normalizedType !== 'media' && (
                    <Input
                      size="xs"
                      variant="contentQuestion"
                      color="solid.900"
                      placeholder={
                        normalizedType === 'paragraph'
                          ? 'Text goes here'
                          : (normalizedType === ContentItemType.LearningCheckbox)
                            ? 'Title goes here'
                            : 'Question goes here'
                      }
                      mb={
                        normalizedType === ContentItemType.LearningCheckbox && isIndividualResponse
                          ? '26px'
                          : showResponses || isIndividualResponse
                            ? '8px'
                            : '26px'
                      }
                      ml="10px"
                      w="90%"
                      value={typeof currentText === 'string' ? currentText : ''}
                      onChange={handleTextChange}
                      isReadOnly={quizMode || showResponses || !isEditing}
                      pointerEvents={quizMode || showResponses || !isEditing ? 'none' : 'auto'}
                      cursor={quizMode || showResponses || !isEditing ? 'default' : 'text'}
                      border="none"
                      bg="transparent"
                      style={{ background: 'transparent' }}
                      _hover={{ bg: 'transparent' }}
                      _focus={{ bg: 'transparent', boxShadow: 'none' }}
                    />
                  )}
                  {renderContent()}
                </Flex>
              )}

              {quizMode &&
                (currentType === 'training_multiple_choice' || currentType === 'training_checkbox') &&
                !isSurveyQuestion && (
                  <Flex position="absolute" top="8px" mt={3.5} right="12px">
                    {/* points display placeholder */}
                  </Flex>
                )}
            </>
          </Flex>

          {showResponses && responseData && responseData.length > 0 && (
            <Box p={4}>
              <Suspense fallback={<Text>Loading chart...</Text>}>
                {chartType === 'bar' ? (
                  <ResponseBarChart data={responseData} />
                ) : (
                  <ResponsePieChart data={responseData} />
                )}
              </Suspense>
            </Box>
          )}

          {isEditing && (
            <CardFooter
              borderTop="1px"
              borderColor={CONTENT_EXPERIENCE.glassBorder}
              px="16px"
              py="12px"
              bg={
                (quizSubmitted || isIndividualResponse) &&
                  (currentType === ContentItemType.TrainingMCQ || currentType === ContentItemType.TrainingCheckbox) &&
                  hasUser &&
                  !isSurveyQuestion && typeof isCorrect === 'boolean'
                  ? !isCorrect
                    ? 'error.0'
                    : 'success.0'
                  : 'white'
              }
              borderRadius="0px 0px 7px 7px"
            >
              {showResponses ? (
                <Flex justifyContent="center" w="100%">
                  <Text fontSize="sm" color="solid.500">
                    Response summary
                  </Text>
                </Flex>
              ) : isIndividualResponse && hasUser ? (
                !isSurveyQuestion &&
                  (currentType === ContentItemType.TrainingMCQ || currentType === ContentItemType.TrainingCheckbox) ? (
                  <Flex align="center" width="100%">
                    {typeof isCorrect === 'boolean' ? (
                      <Icon boxSize="24px" color={!isCorrect ? 'error.100' : 'success.100'} fill="none">
                        {!isCorrect ? <XCircleIcon /> : <CheckCircleFilledIcon />}
                      </Icon>
                    ) : null}
                    <Flex
                      direction="column"
                      ml="10px"
                      justify="center"
                      flex="1"
                      color={typeof isCorrect === 'boolean' ? (!isCorrect ? 'error.100' : 'success.100') : 'solid.500'}
                    >
                      <Text textStyle="h-s">
                        {typeof isCorrect === 'boolean' ? (!isCorrect ? 'Incorrect' : 'Correct') : 'Response recorded'}
                      </Text>
                      <Text textStyle="l-xs" mt="0px">
                        {typeof isCorrect === 'boolean'
                          ? !isCorrect && incorrect_feedback
                            ? incorrect_feedback
                            : isCorrect && correct_feedback
                              ? correct_feedback
                              : 'No feedback available'
                          : ''}
                      </Text>
                    </Flex>
                  </Flex>
                ) : (
                  <Flex align="center" width="100%">
                    <Text fontSize="sm" color="solid.500">
                      Response recorded
                    </Text>
                  </Flex>
                )
              ) : isIndividualResponse && !hasUser ? (
                <Flex justifyContent="center" w="100%">
                  <Text fontSize="sm" color="solid.300">
                    Select a respondent to view feedback
                  </Text>
                </Flex>
              ) : answerSelectionMode ? (
                <Flex justifyContent="space-between" w="100%" align="center">
                  <Text>Choose correct answer</Text>
                  <Flex gap={2}>
                    <Button
                      variant="white"
                      size="xs"
                      onClick={() => {
                        if (mcqRef.current) {
                          mcqRef.current.cancelSelectCorrect()
                        }
                        setAnswerSelectionMode(false)
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="mono"
                      size="xs"
                      onClick={() => {
                        if (mcqRef.current) {
                          mcqRef.current.doneSelectCorrect()
                        }
                        setAnswerSelectionMode(false)
                      }}
                    >
                      Done
                    </Button>
                  </Flex>
                </Flex>
              ) : !quizMode && isEditing ? (
                /* ── Edit-mode footer: drag handle (left) + action buttons (right) ── */
                <Flex justifyContent="space-between" w="100%" align="center">
                  {/* Drag handle — leftmost */}
                  <Box
                    {...(dragHandleProps || {})}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="28px"
                    h="28px"
                    borderRadius="6px"
                    color="solid.400"
                    cursor="grab"
                    _hover={{ color: 'solid.900', bg: 'solid.25' }}
                    _active={{ cursor: 'grabbing' }}
                    flexShrink={0}
                  >
                    <Icon boxSize="18px" fill="none" color="inherit">
                      <DragGridIcon />
                    </Icon>
                  </Box>

                  {/* Action buttons — right */}
                  <Flex align="center">
                    {(currentType === 'training_multiple_choice' || currentType === 'training_checkbox') && (
                      <Button
                        variant="contentIcon"
                        onClick={() => setAnswerSelectionMode(true)}
                        gap="4px"
                        width="fit-content"
                        mr="16px"
                        px={2}
                      >
                        <Icon fill="none" boxSize="16px" color="solid.500">
                          <ClipboardCheckIcon />
                        </Icon>
                        <Text textStyle="l-xs">Set Answer</Text>
                      </Button>
                    )}
                    {currentType !== 'url' && currentType !== 'media' && (
                      <Button variant="contentIcon" mr="16px">
                        <Icon boxSize="16px" color="solid.500">
                          <Copy03Icon />
                        </Icon>
                      </Button>
                    )}
                    <Button variant="contentIcon" onClick={() => handleDelete?.(id)}>
                      <Icon boxSize="16px" color="solid.500">
                        <Trash02Icon />
                      </Icon>
                    </Button>
                  </Flex>
                </Flex>
              ) : !quizSubmitted &&
                (currentType === ContentItemType.TrainingCheckbox ||
                  currentType === ContentItemType.TrainingMCQ ||
                  currentType === ContentItemType.SurveyMCQ ||
                  currentType === ContentItemType.SurveyRating) ? (
                <Flex justifyContent="center" w="100%">
                  <Text fontSize="sm" color="solid.300">
                    Answer the question above
                  </Text>
                </Flex>
              ) : (
                (currentType === ContentItemType.TrainingCheckbox || currentType === ContentItemType.TrainingMCQ) &&
                typeof externalIsCorrect === 'boolean' && (
                  <Flex align="center">
                    <Icon boxSize="24px" color={!isCorrect ? 'error.100' : 'success.100'} fill="none">
                      {!isCorrect ? <XCircleIcon /> : <CheckCircleFilledIcon />}
                    </Icon>
                    <Flex
                      direction="column"
                      ml="10px"
                      justify="center"
                      color={!isCorrect ? 'error.100' : 'success.100'}
                    >
                      <Text textStyle="h-s">{!isCorrect ? 'Incorrect' : 'Correct'}</Text>
                      <Text textStyle="l-xs" mt="0px">
                        {!isCorrect && incorrect_feedback
                          ? incorrect_feedback
                          : isCorrect && correct_feedback
                            ? correct_feedback
                            : 'Feedback goes here...'}
                      </Text>
                    </Flex>
                  </Flex>
                )
              )}
            </CardFooter>
          )}
        </Flex>
      </Card>
    </Flex>
  )
}

export default MasterCard
